import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminClassApplicationDetail,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const ReadOnlyField = ({ label, value, multiline = false, rows = 3 }) => {
  const safeValue = value || '-';

  return (
    <label className={`c-form__field yowan-admin-readonly-field${multiline ? ' yowan-admin-readonly-field--wide' : ''}`}>
      <span className="c-form__label">{label}</span>
      {multiline ? (
        <textarea
          className="c-form__control c-form__control--textarea"
          readOnly
          rows={rows}
          value={safeValue}
        />
      ) : (
        <input className="c-form__control" readOnly type="text" value={safeValue} />
      )}
    </label>
  );
};

const DetailSection = ({ title, lead, children }) => (
  <section className="yowan-admin-detail-panel yowan-class-application-detail__section">
    <header className="yowan-class-application-detail__section-header">
      <h2 className="yowan-admin-detail-panel__title">{title}</h2>
      {lead ? <p className="yowan-admin-detail-panel__lead">{lead}</p> : null}
    </header>
    {children}
  </section>
);

const IdentityObservationList = ({ observations }) => {
  if (!observations.length) {
    return (
      <p className="yowan-admin-detail-panel__meta">
        同一メールアドレスで異なる申込者情報はまだ観測されていません。
      </p>
    );
  }

  return (
    <div className="yowan-class-application-detail__observations">
      {observations.map((observation, index) => (
        <article className="yowan-class-application-detail__observation" key={observation.id || index}>
          <div>
            <p className="yowan-class-application-detail__observation-title">
              {observation.observedName || observation.observedEmail || `観測履歴 ${index + 1}`}
            </p>
            <p className="yowan-admin-detail-panel__meta">
              {[
                observation.observedAt,
                observation.sourceLabel,
                observation.changedFieldsLabel ? `差分: ${observation.changedFieldsLabel}` : '',
              ].filter(Boolean).join(' / ')}
            </p>
          </div>
          <dl className="yowan-class-application-detail__observation-grid">
            <div>
              <dt>ふりがな</dt>
              <dd>{observation.observedNameKana || '-'}</dd>
            </div>
            <div>
              <dt>メールアドレス</dt>
              <dd>{observation.observedEmail || '-'}</dd>
            </div>
            <div>
              <dt>電話番号</dt>
              <dd>{observation.observedPhone || '-'}</dd>
            </div>
            <div>
              <dt>メモ</dt>
              <dd>{observation.note || '-'}</dd>
            </div>
          </dl>
        </article>
      ))}
    </div>
  );
};

const AdminClassApplicationDetailPage = () => {
  const content = yowanPageContent['/admin/events/classes/applications/:applicationId'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { applicationId } = useParams();
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminClassApplicationDetail(applicationId)
      .then((payload) => {
        if (isMounted) {
          setRecord(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecord(null);
          setLoadError('教室申込詳細を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [applicationId]);

  if (!content) {
    return null;
  }

  const classId = record?.classId || '';
  const listHref = classId
    ? `/admin/events/classes/${classId}/applications`
    : '/admin/events/classes';

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={record ? `${record.applicantName} 申込詳細` : content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="applications-list" to={appendYowanPreviewQueryIfNeeded(listHref, preservedDomainSearch)}>
            申込者一覧
          </Link>,
          <Link key="classes-list" to={appendYowanPreviewQueryIfNeeded('/admin/events/classes', preservedDomainSearch)}>
            教室一覧
          </Link>,
          record?.applicantHref ? (
            <Link key="applicant-detail" to={appendYowanPreviewQueryIfNeeded(record.applicantHref, preservedDomainSearch)}>
              問い合わせ・申込者詳細
            </Link>
          ) : null,
        ]}
      />

      <div className="yowan-admin-form c-surface-content">
        {isLoading ? <LoadingSpinner label="教室申込詳細を読み込み中" showLabel /> : null}
        {!isLoading && loadError ? (
          <p className="yowan-admin-form__message">{loadError}</p>
        ) : null}

        {!isLoading && !loadError && record ? (
          <div className="yowan-class-application-detail">
            <DetailSection
              title="申込者情報"
              lead="申込時点の入力内容を表示します。APIがスナップショットを返す場合はスナップショットを優先します。"
            >
              <div className="yowan-class-application-detail__grid">
                <ReadOnlyField label="氏名" value={record.applicantName} />
                <ReadOnlyField label="ふりがな" value={record.applicantNameKana} />
                <ReadOnlyField label="メールアドレス" value={record.applicantEmail} />
                <ReadOnlyField label="電話番号" value={record.applicantPhone} />
                <ReadOnlyField label="申込メッセージ" value={record.message} multiline rows={5} />
              </div>
            </DetailSection>

            {record.hasDistinctCurrentApplicant ? (
              <DetailSection
                title="現在の申込者台帳"
                lead="申込時点の入力内容と現在の申込者台帳に差分がある場合だけ表示します。"
              >
                <div className="yowan-class-application-detail__grid">
                  <ReadOnlyField label="現在の氏名" value={record.currentApplicantName} />
                  <ReadOnlyField label="現在のふりがな" value={record.currentApplicantNameKana} />
                  <ReadOnlyField label="現在のメールアドレス" value={record.currentApplicantEmail} />
                  <ReadOnlyField label="現在の電話番号" value={record.currentApplicantPhone} />
                </div>
              </DetailSection>
            ) : null}

            <DetailSection title="申込内容">
              <div className="yowan-class-application-detail__grid">
                <ReadOnlyField label="教室" value={record.classTitle} />
                <ReadOnlyField label="開催情報" value={record.scheduleLabel} />
                <ReadOnlyField label="希望日" value={record.desiredDate} />
                <ReadOnlyField label="状態" value={record.statusLabel} />
                <ReadOnlyField label="申込日" value={record.createdAt} />
                <ReadOnlyField label="更新日" value={record.updatedAt} />
              </div>
            </DetailSection>

            <DetailSection
              title="同一メールアドレスの観測履歴"
              lead="同一メールアドレスで別名・別ふりがな・別電話番号などが観測された場合に表示します。管理メモとは分けて扱います。"
            >
              <IdentityObservationList observations={record.identityObservations || []} />
            </DetailSection>

            <DetailSection
              title="管理メモ"
              lead="管理者が自由に記録するメモです。同一メールアドレスの観測履歴はこの欄には混ぜません。"
            >
              <ReadOnlyField label="管理メモ" value={record.note} multiline rows={5} />
            </DetailSection>
          </div>
        ) : null}
        {!isLoading && !loadError && !record ? (
          <p className="yowan-admin-form__message">申込詳細はまだ取得できません。</p>
        ) : null}
      </div>
    </AdminBlogShell>
  );
};

export default AdminClassApplicationDetailPage;
